import ResponsiveContainer from "../components/DensityResponsiveContainer";
import { viewportWidth as layoutViewportWidth } from "../utils/displayDensity";
/* eslint-disable */
import * as d3 from "d3";
import Papa from "papaparse";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Area,
  AreaChart,

  XAxis,
  YAxis,
  ReferenceDot, 
} from "recharts";
import TextInputField from "../components/Fields/TextInputField";
import SteamVideoPreview from "../components/SteamVideoPreview";
import { useGlobalData } from "../contexts/DataContext";
import "./mindSHARE/styles/mindshare.css";

/* -------------------- Shared helpers -------------------- */

import { API_BASE } from "../config/api";

async function fetchJSON(pathOrUrl) {
  const isFull = /^https?:\/\//i.test(pathOrUrl);
  const url = isFull
    ? pathOrUrl
    : `${API_BASE}${pathOrUrl.startsWith("/") ? "" : "/"}${pathOrUrl}`;
  const res = await fetch(url, { cache: "no-store" });
  if (!res.ok) return null;
  return res.json();
}

function derivePrice(meta) {
  const raw = meta?.full_price;
  const f2pFlag = meta?.f2p;
  const isF2P =
    String(f2pFlag).toLowerCase() === "true" ||
    f2pFlag === 1 ||
    f2pFlag === "1";

  const n = Number(raw);
  if (isF2P) return { priceValue: 0, isF2P: true };
  if (Number.isFinite(n) && n > 0) return { priceValue: n, isF2P: false };
  return { priceValue: null, isF2P: false };
}

function fmtDateShort(dt) {
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return String(dt);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  return `${mm}/${dd}/${yy}`;
}

function fmtDateTime(dt) {
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return String(dt);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  let hours = d.getHours();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12;
  const mins = String(d.getMinutes()).padStart(2, "0");
  return `${mm}/${dd} ${hours}:${mins}${ampm}`;
}

function dayKeyUTC(dt) {
  const d = new Date(dt);
  if (Number.isNaN(d)) return null;
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const dd = d.getUTCDate();
  return new Date(Date.UTC(y, m, dd)).toISOString().slice(0, 10);
}

function startOfDayUTCISO(dayStr) {
  return `${dayStr}T00:00:00.000Z`;
}

function normalizeArchive(rows) {
  return (rows || [])
    .map((r) => ({
      dt: startOfDayUTCISO(dayKeyUTC(r.dt)),
      ccu: Number(r.ccu) || 0,
    }))
    .sort((a, b) => new Date(a.dt) - new Date(b.dt));
}

function dailyFromLiveSnapshots(rows) {
  const byDay = new Map();
  for (const r of rows || []) {
    const k = dayKeyUTC(r.dt);
    const v = Number(r.ccu);
    if (!k || !Number.isFinite(v) || v < 0) continue;
    const prev = byDay.get(k);
    if (prev === undefined || v > prev) byDay.set(k, v);
  }
  return Array.from(byDay.entries())
    .map(([day, max]) => ({ dt: startOfDayUTCISO(day), ccu: max }))
    .sort((a, b) => new Date(a.dt) - new Date(b.dt));
}

function forwardFill(series) {
  let last = null;
  for (let i = 0; i < series.length; i++) {
    const v = Number(series[i].ccu);
    if (Number.isFinite(v) && v > 0) {
      last = v;
    } else if (last != null) {
      series[i].ccu = last;
    }
  }
  return series;
}

function normalizeLive(rows) {
  const now = Date.now();
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
  const floor = now - sevenDaysMs;

  const out = [];
  for (const r of rows || []) {
    let raw = String(r.dt).trim();
    const hasTZ = /[zZ]$|[+\-]\d\d:?\d\d$/.test(raw);
    if (!raw.includes("T")) raw = raw.replace(" ", "T");
    const d = new Date(hasTZ ? raw : raw + "Z");
    const t = d.getTime();
    if (!Number.isFinite(t) || t < floor) continue;

    let v = Number(r.ccu);
    if (!(Number.isFinite(v) && v > 0)) v = NaN;
    out.push({ dt: d.toISOString(), ccu: v });
  }
  out.sort((a, b) => new Date(a.dt) - new Date(b.dt));
  return forwardFill(out);
}

async function loadLiveSeries(appId, hours = 24 * 7) {
  const live = await fetchJSON(`/live_ccu/${appId}?hours=${hours}`);
  if (Array.isArray(live) && live.length) return normalizeLive(live);
  return [];
}

async function loadDailySeries(appId) {
  const daily = await fetchJSON(`/ccu_daily/${appId}`);
  if (Array.isArray(daily) && daily.length) return normalizeArchive(daily);

  const arch = await fetchJSON(`/ccu_history/${appId}`);
  if (Array.isArray(arch) && arch.length) return normalizeArchive(arch);

  const live = await fetchJSON(`/live_ccu/${appId}`);
  if (Array.isArray(live) && live.length) return dailyFromLiveSnapshots(live);

  return [];
}

function toLocalISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function safeDate(dLike) {
  if (!dLike) return null;
  const d = new Date(dLike);
  return Number.isNaN(d.getTime()) ? null : d;
}
function withinLastNDays(dateLike, n = 7) {
  const d = safeDate(dateLike);
  if (!d) return false;
  const today = new Date();
  const start = new Date(today);
  start.setDate(today.getDate() - n);
  const dISO = toLocalISODate(d);
  const startISO = toLocalISODate(start);
  const endISO = toLocalISODate(today);
  return dISO >= startISO && dISO <= endISO;
}

function releasedWithin7Days(releaseDate) {
  return withinLastNDays(releaseDate, 7);
}

function roundYAxisMax(rawMax) {
  const max = Number(rawMax);
  if (!Number.isFinite(max)) return rawMax;
  const padded = max * 1.1;
  if (padded <= 100) return Math.ceil(padded);
  return Math.ceil(padded / 100) * 100;
}

/* ---- microtrailer / video helpers ---- */

function pickBestVideo(meta) {
  const url =
    meta.microtrailer ||
    meta.micro_trailer ||
    meta.micro_trailer_url ||
    meta.video ||
    meta.trailer ||
    meta.video_url ||
    "";
  return (url || "").trim();
}

function parseYouTubeId(url) {
  if (!url) return null;
  const m = url.match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{6,})/
  );
  return m ? m[1] : null;
}

/* -------------------- SteamMobile component -------------------- */

const STEAM_CHART_COLORS = [
  "#3fa9f5",
  "#F5E6C8",
  "#ff9b70",
  "#33c3eb",
  "#f1726a",
  "#66CCFF",
];

const seriesColor = (i) =>
  STEAM_CHART_COLORS[i % STEAM_CHART_COLORS.length];

export default function SteamMobile() {
  const {
    metadata,
    latestCCU,
    loading: globalLoading,
  } = useGlobalData();

  const [searchTerm, setSearchTerm] = useState("");
  const [games, setGames] = useState([]);
  const [displayedGames, setDisplayedGames] = useState([]);
  const [hiddenGames, setHiddenGames] = useState([]);
  const [steamTotalOnline, setSteamTotalOnline] = useState(0);
  const [loadingPlayers, setLoadingPlayers] = useState(true);

  // reuse chartGames as "expanded cards with history"
  const [chartGames, setChartGames] = useState([]); // { metadata, live, daily }
  const [animSeriesId, setAnimSeriesId] = useState(null);

  // microtrailer overlay
  const [videoGame, setVideoGame] = useState(null); // { name, video }

  const isMobile =
    typeof window !== "undefined" ? layoutViewportWidth() <= 768 : true;

  /* ---------- Players: process metadata + latestCCU ---------- */
  useEffect(() => {
    if (globalLoading || !metadata.length || !latestCCU.length) return;
    try {
      setLoadingPlayers(true);
      const metaMap = new Map();
      metadata.forEach((meta) => {
        const cleanAppId = String(parseInt(meta.app_id)).trim();
        if (cleanAppId && cleanAppId !== "0") metaMap.set(cleanAppId, meta);
      });

      const combined = latestCCU
        .map((ccu) => {
          const cleanAppId = String(parseInt(ccu.app_id)).trim();
          if (cleanAppId === "0") {
            setSteamTotalOnline(parseInt(ccu.ccu?.trim() || "0", 10));
            return null;
          }
          const meta = metaMap.get(cleanAppId);
          if (!meta) return null;

          const { priceValue, isF2P } = derivePrice(meta);

          return {
            app_id: cleanAppId,
            name: meta.name?.trim() || "",
            box_art: meta.box_art?.trim() || "",
            ccu: parseInt(ccu.ccu?.trim() || "0", 10),
            release_date: meta.release_date?.trim() || "",
            full_price: meta.full_price?.trim() || "",
            price_value: priceValue,
            f2p: isF2P,
            short_description: meta.short_description?.trim() || "",
            tags: meta.tags?.split(",").map((tag) => tag.trim()).filter(Boolean) || [],
            video: pickBestVideo(meta),
          };
        })
        .filter(Boolean);

      combined.sort((a, b) => b.ccu - a.ccu);
      setGames(combined);
      setDisplayedGames(combined);
    } finally {
      setLoadingPlayers(false);
    }
  }, [metadata, latestCCU, globalLoading]);

  /* ---------- Filtering (simple: search only) ---------- */
  const filteredPlayers = useMemo(
    () =>
      displayedGames
        .filter((g) => !hiddenGames.includes(g.app_id))
        .filter((g) =>
          g.name.toLowerCase().includes(searchTerm.toLowerCase())
        ),
    [displayedGames, hiddenGames, searchTerm]
  );

  // NO LIMIT – show all filtered games
  const visiblePlayers = filteredPlayers;

  const totalCcu = useMemo(
    () => filteredPlayers.reduce((sum, g) => sum + (g.ccu || 0), 0),
    [filteredPlayers]
  );

  const headerShare =
    steamTotalOnline > 0
      ? ((totalCcu / steamTotalOnline) * 100).toFixed(2)
      : "0.00";

  const statNumberStyle = {
    color: "#fff",
    fontWeight: 900,
    fontSize: isMobile ? "1.1rem" : "1.35rem",
    lineHeight: 1,
  };

  const handleReset = () => {
    setHiddenGames([]);
    setDisplayedGames(games);
    setSearchTerm("");
    setChartGames([]);
    setAnimSeriesId(null);
    setVideoGame(null);
  };

  /* ---------- Small per-card chart helpers ---------- */

  const handleToggleCardChart = async (game) => {
    const appId = String(game.app_id);

    // already expanded → collapse
    const existing = chartGames.find(
      (g) => String(g.metadata.app_id) === appId
    );
    if (existing) {
      setChartGames((prev) =>
        prev.filter((g) => String(g.metadata.app_id) !== appId)
      );
      return;
    }

    // not expanded → add + fetch 7D history
    const metadataForChart = {
      app_id: appId,
      name: game.name,
      box_art: game.box_art,
      release_date: game.release_date,
    };

    setChartGames((prev) => [
      ...prev,
      { metadata: metadataForChart, live: [], daily: [], loading: true },
    ]);

    try {
      const live = await loadLiveSeries(appId);
      setChartGames((prev) =>
        prev.map((g) =>
          String(g.metadata.app_id) === appId
            ? { ...g, live, loading: false }
            : g
        )
      );
      setAnimSeriesId(appId);
      setTimeout(() => {
        setAnimSeriesId((current) => (current === appId ? null : current));
      }, 600);
    } catch (e) {
      console.error("SteamMobile 7D history load failed:", e);
      setChartGames((prev) =>
        prev.map((g) =>
          String(g.metadata.app_id) === appId
            ? { ...g, loading: false }
            : g
        )
      );
    }
  };

  /* ---------- UI ---------- */

  return (
    <div
      style={{
        background: "#000",
        color: "#fff",
        minHeight: "calc(var(--mg-vh) * 100)",
      }}
    >
      <div
        style={{
          maxWidth: 480,
          margin: "0 auto",
          padding: "12px 10px 24px",
        }}
      >
        {/* Top: Steam logo + reset (app shell header handled in App.jsx) */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/Steam Logo.png`}
            alt="Steam"
            style={{ width: 150, height: "auto", display: "block" }}
          />
          <button
            type="button"
            onClick={handleReset}
            style={{
              borderRadius: 999,
              border: "1px solid #444",
              background: "#111",
              color: "#eee",
              padding: "4px 10px",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            ↻ Reset
          </button>
        </div>

        {/* Search bar */}
        <div style={{ marginBottom: 10 }}>
          <TextInputField
            placeholder="Search games..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Header stats */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 12,
            alignItems: "baseline",
            marginBottom: 12,
          }}
        >
          <div style={{ fontSize: 13, color: "#aaa" }}>
            <span style={statNumberStyle}>
              {filteredPlayers.length.toLocaleString()}
            </span>
            <span style={{ marginLeft: 4 }}>Games</span>
          </div>
          <div style={{ fontSize: 13, color: "#aaa" }}>
            <span style={statNumberStyle}>
              {totalCcu.toLocaleString()}
            </span>
            <span style={{ marginLeft: 4 }}>Playing Now</span>
          </div>
          <div style={{ fontSize: 13, color: "#aaa" }}>
            <span style={statNumberStyle}>
              {headerShare === "100.00" ? "100" : headerShare}%
            </span>
            <span style={{ marginLeft: 4 }}>of Online Players</span>
          </div>
        </div>

        {/* Cards – single premium column, artwork-forward */}
        {globalLoading || loadingPlayers ? (
          <div style={{ marginTop: 20, fontSize: 13, color: "#aaa" }}>
            Loading Steam data…
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
              marginTop: 16,
            }}
          >
            {visiblePlayers.map((g, i) => {
              const share =
                totalCcu > 0
                  ? ((g.ccu / totalCcu) * 100).toFixed(2)
                  : "0.00";
              const isRecent = releasedWithin7Days(g.release_date);

            const chartEntry = chartGames.find(
  (cg) => String(cg.metadata.app_id) === String(g.app_id)
);
const inChart = !!chartEntry;

// 🔹 Build 7D history for the card chart
let history = [];
let historyMax = 0;
let peakPoint = null;

if (chartEntry && Array.isArray(chartEntry.live) && chartEntry.live.length) {
  const raw = chartEntry.live;

  // Light down-sampling so we don’t overload the tiny card
  const MAX_POINTS = 220;
  const step = Math.max(1, Math.ceil(raw.length / MAX_POINTS));

  history = raw
    .filter((_, idx) => idx % step === 0)
    .map((row, idx) => {
      const ccu = Number(row.ccu) || 0;
      if (ccu > historyMax) historyMax = ccu;
      if (!peakPoint || ccu > peakPoint.ccu) {
        peakPoint = { idx, ccu };
      }
      return { idx, ccu };
    });
}


              return (
                <div
                  key={g.app_id}
                  style={{
                    borderRadius: 18,
                    overflow: "hidden",
                    background:
                      "radial-gradient(circle at 0 0, rgba(63,169,245,0.18), transparent 60%), #050609",
                    border: isRecent
                      ? "1px solid rgba(255,215,0,0.9)"
                      : "1px solid rgba(255,255,255,0.08)",
                    boxShadow: isRecent
                      ? "0 16px 40px rgba(0,0,0,0.9)"
                      : "0 10px 28px rgba(0,0,0,0.75)",
                    maxWidth: 420,
                    margin: "0 auto",
                  }}
                >
                  {/* Artwork (tap to open microtrailer if available) */}
                  <div
                    style={{
                      position: "relative",
                      cursor: g.video ? "pointer" : "default",
                    }}
                    onClick={() => {
                      if (g.video) {
                        setVideoGame({ name: g.name, video: g.video });
                      }
                    }}
                  >
                    {g.box_art ? (
                      <img
                        src={g.box_art}
                        alt={g.name}
                        style={{
                          width: "100%",
                          aspectRatio: "16/9",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: "100%",
                          aspectRatio: "16/9",
                          background:
                            "linear-gradient(135deg,#1a1a1a,#222)",
                        }}
                      />
                    )}

                    {/* Darken + inline chart when expanded */}
                    {inChart && (
                      <>
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            background: "rgba(0,0,0,0.76)",
                            zIndex: 1,
                          }}
                        />
                        <div
                          style={{
                            position: "absolute",
                            left: 10,
                            right: 10,
                            top: 24,
                            bottom: 26,
                            zIndex: 2,
                            pointerEvents: "none",
                          }}
                        >
                          {history.length ? (
  <ResponsiveContainer width="100%" height="100%">
    <AreaChart
      data={history}
      margin={{ top: 4, right: 0, left: 0, bottom: 0 }}
    >
      <XAxis dataKey="idx" hide />

      {/* 🔹 Y axis always from 0 → padded max */}
      <YAxis
        hide
        domain={[
          0,
          historyMax > 0 ? historyMax * 1.05 : 1, // 5% headroom
        ]}
      />

      {/* White organic line */}
      <Area
        type="monotone"
        dataKey="ccu"
        stroke="#ffffff"
        strokeWidth={3}
        fill="rgba(255,255,255,0.10)"
        fillOpacity={0.25}
        dot={false}
        isAnimationActive={false}
      />

      {/* 🔹 Peak CCU label at the highest point */}
      {peakPoint && (
        <ReferenceDot
          x={peakPoint.idx}
          y={peakPoint.ccu}
          r={0} // invisible dot; just the label
          ifOverflow="extendDomain"
          label={{
            value: peakPoint.ccu.toLocaleString(),
            position: "top",
            fill: "#ffffff",
            fontSize: 9,
            fontWeight: 800,
          }}
        />
      )}
    </AreaChart>
  </ResponsiveContainer>
) : (
  <div
    style={{
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      color: "#bbb",
    }}
  >
    Loading 7D history…
  </div>
)}

                        </div>
                      </>
                    )}

                    {/* Rank badge */}
                    <div
                      style={{
                        position: "absolute",
                        top: 6,
                        left: 6,
                        background: "rgba(0,0,0,0.75)",
                        color: "#fff",
                        fontSize: 11,
                        padding: "0 8px",
                        borderRadius: 999,
                        lineHeight: "18px",
                        fontWeight: 800,
                        zIndex: 3,
                      }}
                    >
                      #{i + 1}
                    </div>

                    {/* Hide button */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setHiddenGames((prev) => [...prev, g.app_id]);
                      }}
                      style={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                        background: "rgba(0,0,0,0.75)",
                        border: "none",
                        color: "#fff",
                        fontSize: 11,
                        padding: "0 8px",
                        borderRadius: 999,
                        lineHeight: "18px",
                        fontWeight: 800,
                        cursor: "pointer",
                        zIndex: 3,
                      }}
                    >
                      ×
                    </button>

                    {/* Bottom overlay – name + + button + stats */}
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        padding: "8px 10px",
                        background:
                          "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.9) 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        zIndex: 4,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          minWidth: 0,
                        }}
                      >
                        {/* + / ✓ toggle */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleToggleCardChart(g);
                          }}
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: 999,
                            border: inChart
                              ? "1px solid #3fa9f5"
                              : "1px solid #444",
                            background: inChart ? "#3fa9f5" : "#111",
                            color: inChart ? "#000" : "#cfd8e3",
                            fontSize: 13,
                            lineHeight: "13px",
                            fontWeight: 900,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {inChart ? "✓" : "+"}
                        </button>

                        {/* Name */}
                        <div
                          style={{
                            fontWeight: 800,
                            fontSize: 15,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            color: "#fff",
                          }}
                        >
                          {g.name}
                        </div>
                      </div>

                      {/* Stats: CCU – share% (bold numbers) */}
                      <div
                        style={{
                          fontSize: 11,
                          color: "#e5e7eb",
                          textAlign: "right",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                      >
                        <span style={{ fontWeight: 800 }}>
                          {g.ccu.toLocaleString()}
                        </span>
                        <span> – </span>
                        <span style={{ fontWeight: 800 }}>
                          {share}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Microtrailer overlay (tap artwork to open) */}
        {videoGame && (
          <div
            onClick={() => setVideoGame(null)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.85)",
              zIndex: 9999,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              padding: "0 8px 16px",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                maxWidth: 480,
                background: "#050608",
                borderRadius: 16,
                border: "1px solid #333",
                overflow: "hidden",
                boxShadow: "0 18px 40px rgba(0,0,0,0.9)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px 12px",
                  borderBottom: "1px solid #222",
                }}
              >
                <div
                  style={{
                    fontWeight: 800,
                    fontSize: 14,
                  }}
                >
                  {videoGame.name}
                </div>
                <button
                  type="button"
                  onClick={() => setVideoGame(null)}
                  style={{
                    border: "none",
                    background: "transparent",
                    color: "#fff",
                    fontSize: 16,
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              </div>

              <div
                style={{
                  width: "100%",
                  background: "#000",
                }}
              >
                <SteamVideoPreview tags={videoGame.tags} title={videoGame.name}>
                  {(() => {
                    const ytId = parseYouTubeId(videoGame.video);
                    if (ytId) {
                      return (
                        <iframe
                          title={`${videoGame.name} trailer`}
                          src={`https://www.youtube.com/embed/${ytId}?autoplay=1&mute=1&playsinline=1&controls=1&rel=0`}
                          allow="autoplay; encrypted-media; picture-in-picture"
                          allowFullScreen
                          style={{
                            width: "100%",
                            aspectRatio: "16/9",
                            border: "none",
                            display: "block",
                          }}
                        />
                      );
                    }
                    return (
                      <video
                        src={videoGame.video}
                        autoPlay
                        muted
                        controls
                        playsInline
                        style={{
                          width: "100%",
                          aspectRatio: "16/9",
                          display: "block",
                        }}
                      />
                    );
                  })()}
                </SteamVideoPreview>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
